import { pdfjs } from 'react-pdf';

import { getEnv } from "@/config/env";

// Configure the PDF.js worker so page-count validation works without
// requiring the PdfViewer component to have been mounted first.
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export const processFileName = (fileName: string): string => {
  const parts = fileName.split(".");
  const extension = parts.pop();
  const nameWithoutExtension = parts.join(".");
  const processedName = nameWithoutExtension
    .replaceAll(/\s+/g, "_")
    .replaceAll('.', "_")
    .replaceAll('\'', "_")
    .replaceAll(/[^a-zA-Z0-9_-]/g, "_");
  return processedName + "." + extension;
};

// Upload limits, configurable per environment via env-config.js (defaults apply when unset)
export const MAX_FILE_SIZE_MB = Number(getEnv("VITE_FILE_UPLOAD_MAX_SIZE_MB") || 100);
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
export const MAX_PAGE_COUNT = Number(getEnv("VITE_FILE_UPLOAD_MAX_PAGES") || 500);
// All selected files are sent in one upload request, so their combined size is capped too;
// larger batches exceed the gateway's 10-minute timeout
export const MAX_TOTAL_UPLOAD_SIZE_GB = Number(getEnv("VITE_FILE_UPLOAD_MAX_TOTAL_SIZE_GB") || 0.5);
export const MAX_TOTAL_UPLOAD_SIZE_BYTES = MAX_TOTAL_UPLOAD_SIZE_GB * 1024 * 1024 * 1024;

export const UPLOAD_LIMIT_ERROR_MESSAGE =
  `Upload failed: Your files must be ${MAX_PAGE_COUNT} pages or fewer and ${MAX_FILE_SIZE_MB} MB or smaller, and ${MAX_TOTAL_UPLOAD_SIZE_GB} GB or less in total. Please reduce the file size/page count or upload fewer files and try again.`;

export const ALLOWED_EXTENSIONS = ['pdf'];

// CEM artifact upload — kept separate from ALLOWED_EXTENSIONS above, which
// must stay PDF-only for the Documents tab.
//
// .xlsb is allowed for now: nothing actually parses the uploaded file yet
// (build_dummy_artifacts is fully fake), so there's no real reason to reject
// it today. Revisit once real Appendix Sheet parsing lands — .xlsb's binary
// format isn't reliably parseable by standard libraries, so that's the point
// to decide whether to require .xlsm/.xlsx instead.
export const ALLOWED_CEM_EXTENSIONS = ['xlsx', 'xlsm', 'xlsb'];
export const CEM_INVALID_TYPE_MESSAGE =
  "Only .xlsx, .xlsm, or .xlsb files are supported for CEM model uploads.";

export interface CemFileValidationResult {
  valid: boolean;
  message?: string;
}

export function validateCemFile(file: File): CemFileValidationResult {
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (!extension || !ALLOWED_CEM_EXTENSIONS.includes(extension)) {
    return { valid: false, message: CEM_INVALID_TYPE_MESSAGE };
  }
  return { valid: true };
}

export interface FileValidationError {
  fileName: string;
  exceedsSize: boolean;
  exceedsPages: boolean;
  invalidType: boolean;
  exceedsTotalSize?: boolean;
}

/** Human-readable reason a file was rejected, shown next to its name. */
export function getFileErrorReason(err: FileValidationError): string {
  if (err.invalidType) return "unsupported file type (only PDF is accepted)";
  if (err.exceedsTotalSize) return `would take this upload over the ${MAX_TOTAL_UPLOAD_SIZE_GB} GB total limit`;
  if (err.exceedsSize && err.exceedsPages) {
    return `exceeds both the ${MAX_FILE_SIZE_MB} MB size limit and the ${MAX_PAGE_COUNT}-page limit`;
  }
  if (err.exceedsSize) return `exceeds the ${MAX_FILE_SIZE_MB} MB size limit`;
  return `exceeds the ${MAX_PAGE_COUNT}-page limit`;
}

/**
 * Apply the total upload size limit on top of files already selected.
 * Files are taken in order and accepted while they still fit; any file that
 * would push the total over MAX_TOTAL_UPLOAD_SIZE_BYTES is rejected.
 */
export function applyTotalSizeLimit(
  files: File[],
  existingBytes: number,
): { accepted: File[]; rejected: FileValidationError[] } {
  const accepted: File[] = [];
  const rejected: FileValidationError[] = [];
  let totalBytes = existingBytes;

  for (const file of files) {
    if (totalBytes + file.size <= MAX_TOTAL_UPLOAD_SIZE_BYTES) {
      accepted.push(file);
      totalBytes += file.size;
    } else {
      rejected.push({
        fileName: file.name,
        exceedsSize: false,
        exceedsPages: false,
        invalidType: false,
        exceedsTotalSize: true,
      });
    }
  }

  return { accepted, rejected };
}

/**
 * Validate a single file against upload limits.
 * Checks file extension (PDF only), file size (MAX_FILE_SIZE_MB), and page count (MAX_PAGE_COUNT).
 * Returns null if the file passes validation, or an error object describing the violation.
 */
export async function validateFile(file: File): Promise<FileValidationError | null> {
  const extension = file.name.split('.').pop()?.toLowerCase();
  const invalidType = !extension || !ALLOWED_EXTENSIONS.includes(extension);

  // If the file type is not allowed, reject immediately without further checks
  if (invalidType) {
    return {
      fileName: file.name,
      exceedsSize: false,
      exceedsPages: false,
      invalidType: true,
    };
  }

  const exceedsSize = file.size > MAX_FILE_SIZE_BYTES;
  let exceedsPages = false;

  // Check page count for PDF files
  if (extension === 'pdf') {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
      if (pdf.numPages > MAX_PAGE_COUNT) {
        exceedsPages = true;
      }
      pdf.destroy();
    } catch {
      // If we can't read the PDF, skip page count validation
      // (the file may still fail at upload time for other reasons)
    }
  }

  if (exceedsSize || exceedsPages) {
    return {
      fileName: file.name,
      exceedsSize,
      exceedsPages,
      invalidType: false,
    };
  }

  return null;
}

/**
 * Validate multiple files against upload limits.
 * Returns an array of validation errors (empty if all files pass).
 */
export async function validateFiles(files: File[]): Promise<FileValidationError[]> {
  const results = await Promise.all(files.map(validateFile));
  return results.filter((r): r is FileValidationError => r !== null);
}
